import React, { useState, useEffect } from 'react';
import { getAdminPassword, getAdminHeader } from '../shared/authConfig';
import { londonDateKey } from '../shared/dateUtils';
import './KVManagement.css';

interface KVEntry {
    key: string;
    value: any;
    metadata?: any;
}

const KVManagement: React.FC = () => {
    const [entries, setEntries] = useState<KVEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [importData, setImportData] = useState('');
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

    useEffect(() => {
        fetchAllEntries();
    }, []);

    const fetchAllEntries = async () => {
        setLoading(true);
        setError(null);
        try {
            // First, get list of all keys in the PRODUCTS namespace
            const listResponse = await fetch('/api/kv-list', {
                headers: {
                    [getAdminHeader()]: getAdminPassword() || ''
                }
            });
            if (!listResponse.ok) {
                throw new Error('Failed to fetch key list');
            }

            const listData = await listResponse.json() as { keys: Array<{ name: string; metadata?: any }> };
            const fetchedEntries: KVEntry[] = [];

            // Fetch value for each key
            for (const keyInfo of listData.keys) {
                const key = keyInfo.name;
                try {
                    const response = await fetch(`/api/kv/${key}`, {
                        headers: {
                            [getAdminHeader()]: getAdminPassword() || ''
                        }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        fetchedEntries.push({
                            key,
                            value: data,
                            metadata: keyInfo.metadata
                        });
                    }
                } catch (err) {
                    console.warn(`Failed to fetch ${key}:`, err);
                }
            }

            setEntries(fetchedEntries);
        } catch (err) {
            setError('Failed to fetch KV entries');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        const exportData = entries
            .filter(entry => selectedKeys.size === 0 || selectedKeys.has(entry.key))
            .reduce((acc, entry) => {
                acc[entry.key] = entry.value;
                return acc;
            }, {} as Record<string, any>);

        const dataStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `kv-export-${londonDateKey()}.json`;
        link.click();
        URL.revokeObjectURL(url);

        setSuccess('Data exported successfully!');
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleCopyToClipboard = () => {
        const exportData = entries
            .filter(entry => selectedKeys.size === 0 || selectedKeys.has(entry.key))
            .reduce((acc, entry) => {
                acc[entry.key] = entry.value;
                return acc;
            }, {} as Record<string, any>);

        const dataStr = JSON.stringify(exportData, null, 2);
        navigator.clipboard.writeText(dataStr);

        setSuccess('Data copied to clipboard!');
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleImport = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = JSON.parse(importData);

            let successCount = 0;
            let errorCount = 0;
            const keysToImport = Object.keys(data);

            // Only import the keys that are provided in the JSON
            for (const key of keysToImport) {
                try {
                    const response = await fetch(`/api/kv/${key}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            [getAdminHeader()]: getAdminPassword() || ''
                        },
                        body: JSON.stringify(data[key])
                    }); if (response.ok) {
                        successCount++;
                    } else {
                        errorCount++;
                        console.error(`Failed to import ${key}:`, await response.text());
                    }
                } catch (err) {
                    errorCount++;
                    console.error(`Failed to import ${key}:`, err);
                }
            }

            setSuccess(`Imported ${successCount} of ${keysToImport.length} keys${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
            setImportData('');
            await fetchAllEntries();
        } catch (err) {
            setError('Invalid JSON format');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEntry = async (key: string) => {
        if (!confirm(`Are you sure you want to delete "${key}"?`)) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`/api/kv/${key}`, {
                method: 'DELETE',
                headers: {
                    [getAdminHeader()]: getAdminPassword() || ''
                }
            });

            if (response.ok) {
                setSuccess(`Deleted "${key}" successfully`);
                await fetchAllEntries();
            } else {
                setError(`Failed to delete "${key}"`);
            }
        } catch (err) {
            setError('Failed to delete entry');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleKeySelection = (key: string) => {
        const newSelection = new Set(selectedKeys);
        if (newSelection.has(key)) {
            newSelection.delete(key);
        } else {
            newSelection.add(key);
        }
        setSelectedKeys(newSelection);
    };

    const toggleSelectAll = () => {
        if (selectedKeys.size === entries.length) {
            setSelectedKeys(new Set());
        } else {
            setSelectedKeys(new Set(entries.map(e => e.key)));
        }
    };

    return (
        <div className="kv-management">
            <h2>KV Storage Management</h2>
            <p className="kv-description">
                Export data from this environment, copy it, and import it to another environment (local ↔ production).
            </p>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="kv-actions">
                <div className="action-group">
                    <h3>Export Data from PRODUCTS Namespace</h3>
                    <div className="button-group">
                        <button
                            onClick={toggleSelectAll}
                            className="button-secondary"
                            disabled={loading || entries.length === 0}
                        >
                            {selectedKeys.size === entries.length ? 'Deselect All' : 'Select All'}
                        </button>
                        <button
                            onClick={handleCopyToClipboard}
                            disabled={loading || entries.length === 0}
                        >
                            📋 Copy to Clipboard
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={loading || entries.length === 0}
                        >
                            💾 Download JSON
                        </button>
                        <button
                            onClick={fetchAllEntries}
                            className="button-secondary"
                            disabled={loading}
                        >
                            🔄 Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="kv-entries">
                <h3>Current Entries ({selectedKeys.size > 0 ? `${selectedKeys.size} selected` : `${entries.length} total`})</h3>
                {loading && <div className="loading">Loading...</div>}
                {!loading && entries.length === 0 && (
                    <div className="no-entries">No entries found</div>
                )}
                {!loading && entries.length > 0 && (
                    <div className="entries-list">
                        {entries.map(entry => (
                            <div key={entry.key} className={`entry-card ${selectedKeys.has(entry.key) ? 'selected' : ''}`}>
                                <div className="entry-header">
                                    <input
                                        type="checkbox"
                                        checked={selectedKeys.has(entry.key)}
                                        onChange={() => toggleKeySelection(entry.key)}
                                    />
                                    <h4>{entry.key}</h4>
                                    <button
                                        onClick={() => handleDeleteEntry(entry.key)}
                                        className="delete-button"
                                        disabled={loading}
                                    >
                                        🗑️
                                    </button>
                                </div>
                                <div className="entry-preview">
                                    <pre>{JSON.stringify(entry.value, null, 2).slice(0, 200)}...</pre>
                                </div>
                                <div className="entry-meta">
                                    Size: {JSON.stringify(entry.value).length} bytes
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="kv-import">
                <h3>Import Data</h3>
                <p className="import-hint">
                    Paste JSON data exported from another environment. <strong>Only the keys provided in the JSON will be updated.</strong> Other keys will remain unchanged.
                </p>
                <textarea
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    placeholder='{"products": [...], "stocks": [...], ...}'
                    rows={10}
                    disabled={loading}
                />
                <button
                    onClick={handleImport}
                    disabled={loading || !importData.trim()}
                    className="import-button"
                >
                    📥 Import Data (Only Provided Keys)
                </button>
            </div>
        </div>
    );
};

export default KVManagement;
